import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import * as Sentry from "@sentry/react";

export const fetchColorTemplateData = createAsyncThunk(
  "createColorTemplate",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/createColorTemplate`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw rejectWithValue((error as AxiosError)?.response?.data);
    }
  },
);

export const generateColorArtData = createAsyncThunk(
  "generateColorArt",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_NEW_API_URL}/generateColorArt`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
          },
        },
      );
      const item = response.data?.generate_color_data;
      if (item) {
        item.backimage = await convertImageUrlToBase64(item.backimage);
        item.frontimage = await convertImageUrlToBase64(item.frontimage);
        item.leftimage = await convertImageUrlToBase64(item.leftimage);
        if (
          item.neckimage !=
          "https://d1shqo97l1jy20.cloudfront.net/uploads/neck.png"
        ) {
          item.neckimage = await convertImageUrlToBase64(item.neckimage);
        }
        item.rightimage = await convertImageUrlToBase64(item.rightimage);
        // item.backimage = await convertImageUrlToBase64(
        //   `https://dummyimage.com/600x400/000/fff`,
        // );
        // item.frontimage = await convertImageUrlToBase64(
        //   `https://dummyimage.com/600x400/000/fff`,
        // );
        // item.leftimage = await convertImageUrlToBase64(
        //   `https://dummyimage.com/600x400/000/fff`,
        // );
        // item.neckimage = await convertImageUrlToBase64(
        //   `https://dummyimage.com/600x400/000/fff`,
        // );
        // item.rightimage = await convertImageUrlToBase64(
        //   `https://dummyimage.com/600x400/000/fff`,
        // );
      }

      return item;

      // return response.data?.generate_color_data;
    } catch (error) {
      throw rejectWithValue((error as AxiosError)?.response?.data);
    }
  },
);

export const base64ToPngData = createAsyncThunk(
  "custom_mockup_image_base64_to_png",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      Sentry.addBreadcrumb({
        category: "api",
        message: "Calling custom_mockup_image_base64_to_png",
        level: "info",
      });
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/custom_mockup_image_base64_to_png`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          formDataKeys: Array.from(formData.keys()),
        },
      });
      throw rejectWithValue((error as AxiosError)?.response?.data);
    }
  },
);

interface ColorTemplateState {
  loadingColorTemplate: boolean;
  loadingGenerateColorArt: boolean;
  loadingBase64ToPng: boolean;
  activeColor: any[];
  colorOrderForSize: string[];
  dataColorTemplate: any;
  dataGenerateColorArt: any[];
  error: string;
}

const initialState: ColorTemplateState = {
  loadingColorTemplate: false,
  loadingGenerateColorArt: false,
  loadingBase64ToPng: false,
  activeColor: [],
  colorOrderForSize: [],
  dataColorTemplate: null,
  dataGenerateColorArt: [],
  error: "",
};

export const colorTemplateSlice = createSlice({
  name: "colorTemplate",
  initialState,
  reducers: {
    setActiveColor: (state, action) => {
      state.activeColor.unshift(action.payload);
      state.colorOrderForSize.unshift(action.payload.id);
    },
    setCurrentActiveColor: (state) => {
      state.activeColor = [state.activeColor[0]];
      state.colorOrderForSize = [state.activeColor[0]?.id];
      state.dataGenerateColorArt = [];
    },
    removeActiveColor: (state, action) => {
      state.activeColor = state.activeColor.filter(
        (color) => color.id !== action.payload,
      );
      state.colorOrderForSize = state.colorOrderForSize.filter(
        (color) => color !== action.payload,
      );
      state.dataGenerateColorArt = state.dataGenerateColorArt.filter(
        (color) => color.id !== action.payload,
      );
    },
    resetActiveColor: (state) => {
      state.activeColor = [];
      state.colorOrderForSize = [];
      state.dataGenerateColorArt = [];
    },
    setFocusColor: (state, action) => {
      state.activeColor.sort((a, b) => {
        if (a.id === action.payload) return -1;
        if (b.id === action.payload) return 1;
        return 0;
      });

      state.dataGenerateColorArt.sort((a, b) => {
        if (a.id === action.payload) return -1;
        if (b.id === action.payload) return 1;
        return 0;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchColorTemplateData.pending, (state) => {
        state.loadingColorTemplate = true;
        state.error = "";
      })
      .addCase(fetchColorTemplateData.fulfilled, (state, action) => {
        state.loadingColorTemplate = false;
        state.dataColorTemplate = action.payload;
        state.error = "";
      })
      .addCase(fetchColorTemplateData.rejected, (state, action) => {
        state.loadingColorTemplate = false;
        state.dataColorTemplate = null;
        state.error =
          action.error.message || "Failed to fetch color template data";
      });

    builder
      .addCase(generateColorArtData.pending, (state) => {
        state.loadingGenerateColorArt = true;
        state.error = "";
      })
      .addCase(generateColorArtData.fulfilled, (state, action) => {
        state.loadingGenerateColorArt = false;
        state.dataGenerateColorArt.unshift(action.payload);
        state.error = "";
      })
      .addCase(generateColorArtData.rejected, (state, action) => {
        state.loadingGenerateColorArt = false;
        state.dataGenerateColorArt = [];
        state.error =
          action.error.message || "Failed to generate color art data";
      });

    builder
      .addCase(base64ToPngData.pending, (state) => {
        state.loadingBase64ToPng = true;
        state.error = "";
      })
      .addCase(base64ToPngData.fulfilled, (state, action) => {
        state.loadingBase64ToPng = false;
        state.activeColor[0].mockupImage = action.payload.imageUrl;
        state.error = "";
      })
      .addCase(base64ToPngData.rejected, (state, action) => {
        // Keep the shirt pictures: clearing them blanked the mockup and made a retry impossible
        state.loadingBase64ToPng = false;
        state.error =
          action.error.message || "Failed to fetch Base64 To Png data";
      });
  },
});

const getMimeTypeFromUrl = (url: string): string => {
  if (url.endsWith(".png")) return "image/png";
  if (url.endsWith(".jpg") || url.endsWith(".jpeg")) return "image/jpeg";
  if (url.endsWith(".gif")) return "image/gif";
  return "image/png";
};

const convertImageUrlToBase64 = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);
    const buffer = await response.arrayBuffer();

    const mimeType = getMimeTypeFromUrl(url);

    const blob = new Blob([buffer], { type: mimeType });

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
};

export const {
  setActiveColor,
  setCurrentActiveColor,
  removeActiveColor,
  resetActiveColor,
  setFocusColor,
} = colorTemplateSlice.actions;

export default colorTemplateSlice.reducer;
